import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useState } from "react";
import { MoneyUnit } from "@/modules/types/enum";
import { useClientTranslations } from "@/lib/translate";
import { Input } from "../commons/Input";
import {
  Briefcase01Icon,
  Cancel01Icon,
  CheckmarkCircle01Icon,
  Dollar01Icon,
  Message01Icon,
} from "hugeicons-react";
import { Textarea } from "../commons/Textarea";
import CircularProgress from "../commons/CircularProgress";

interface ApplyJobModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    coverLetter: string,
    minDesiredSalary: number,
    maxDesiredSalary: number,
    moneyUnit: MoneyUnit
  ) => void;
  loading?: boolean;
}

interface ValidationErrors {
  coverLetter?: string;
  minSalary?: string;
  maxSalary?: string;
  [key: string]: string | undefined;
}

const formatNumberWithCommas = (value: number | string): string => {
  if (value === undefined || value === null || value === 0) return "";
  const numString = String(value).replace(/\D/g, "");
  if (!numString) return "";
  return new Intl.NumberFormat("en-US").format(Number(numString));
};

const parseNumberFromCommas = (value: string): number => {
  const numString = value.replace(/\D/g, "");
  return numString ? parseInt(numString, 10) : 0;
};

const ApplyJobModal = ({
  open,
  onClose,
  onSubmit,
  loading,
}: ApplyJobModalProps) => {
  const { t } = useClientTranslations("job-apply");
  const [coverLetter, setCoverLetter] = useState("");
  const [minSalary, setMinSalary] = useState(0);
  const [maxSalary, setMaxSalary] = useState(0);
  const [moneyUnit] = useState<MoneyUnit>(MoneyUnit.vnd);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Validate cover letter
    if (!coverLetter.trim()) {
      newErrors.coverLetter = t("coverLetterRequired");
    } else if (coverLetter.length > 1000) {
      newErrors.coverLetter = t("coverLetterTooLong");
    }

    // Validate min salary
    if (minSalary < 1000000) {
      newErrors.minSalary = t("minSalaryLessThan1M");
    }

    // Validate max salary only if provided
    if (maxSalary > 0 && maxSalary <= minSalary) {
      newErrors.maxSalary = t("maxSalaryMustBeGreaterThanMin");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    validateForm();
  };

  const handleFieldChange = (fieldName: string, value: string | number) => {
    if (fieldName === "coverLetter") {
      setCoverLetter(value as string);
    } else if (fieldName === "minSalary") {
      setMinSalary(value as number);
    } else if (fieldName === "maxSalary") {
      setMaxSalary(value as number);
    }

    // Validate on change if field has been touched
    if (touched[fieldName]) {
      validateForm();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      coverLetter: true,
      minSalary: true,
      maxSalary: true,
    });

    if (validateForm()) {
      onSubmit(coverLetter, minSalary, maxSalary, moneyUnit);
    }
  };

  const getFieldClassName = (fieldName: string, baseClassName: string) => {
    const hasError = errors[fieldName] && touched[fieldName];

    if (hasError) {
      return `${baseClassName} border-red-300 focus:border-red-500 focus:ring-red-500/20`;
    }
    return baseClassName;
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-300 p-4">
        <DialogPanel className="w-full max-w-2xl max-h-[90vh] transform overflow-hidden rounded-3xl bg-white shadow-2xl transition-all duration-300 flex flex-col">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-primary to-primaryDark px-4 sm:px-8 py-4 sm:py-6 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <Briefcase01Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <DialogTitle className="text-lg sm:text-2xl font-bold truncate">
                    {t("applyForJob")}
                  </DialogTitle>
                  <p className="text-white/80 text-xs sm:text-sm line-clamp-2">
                    {t("applyForJobDesc")}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors flex-shrink-0"
              >
                <Cancel01Icon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Cover Letter Section */}
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-blue-100">
                    <Message01Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                  </div>
                  <label
                    className="text-base sm:text-lg font-semibold text-gray-900"
                    htmlFor="cover-letter"
                  >
                    {t("coverLetter")} *
                  </label>
                </div>
                <div className="relative">
                  <Textarea
                    id="cover-letter"
                    className={getFieldClassName(
                      "coverLetter",
                      "w-full min-h-[100px] sm:min-h-[120px] rounded-xl border-2 border-gray-200 bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 text-gray-900 placeholder-gray-500 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200 resize-none text-sm sm:text-base"
                    )}
                    value={coverLetter}
                    onChange={(e) =>
                      handleFieldChange("coverLetter", e.target.value)
                    }
                    onBlur={() => handleFieldBlur("coverLetter")}
                    placeholder={t("coverLetterPlaceholder")}
                    required
                    error={
                      errors.coverLetter && touched.coverLetter
                        ? errors.coverLetter
                        : undefined
                    }
                  />
                  <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 text-xs text-gray-400">
                    {coverLetter.length}/1000
                  </div>
                </div>
              </div>

              {/* Salary Section */}
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-green-100">
                    <Dollar01Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600" />
                  </div>
                  <label className="text-base sm:text-lg font-semibold text-gray-900">
                    {t("expectedSalary")} (vnd)
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5 sm:space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="min-salary"
                    >
                      {t("minDesiredSalary")} *
                    </label>
                    <div className="relative">
                      <Input
                        id="min-salary"
                        type="text"
                        className={getFieldClassName(
                          "minSalary",
                          "w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 text-gray-900 placeholder-gray-500 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm sm:text-base"
                        )}
                        value={formatNumberWithCommas(minSalary)}
                        onChange={(e) =>
                          handleFieldChange("minSalary", parseNumberFromCommas(e.target.value))
                        }
                        placeholder="0"
                        required
                        error={
                          errors.minSalary && touched.minSalary
                            ? errors.minSalary
                            : undefined
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <label
                      className="block text-sm font-medium text-gray-700"
                      htmlFor="max-salary"
                    >
                      {t("maxDesiredSalary")}
                    </label>
                    <div className="relative">
                      <Input
                        id="max-salary"
                        type="text"
                        className={getFieldClassName(
                          "maxSalary",
                          "w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 text-gray-900 placeholder-gray-500 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-sm sm:text-base"
                        )}
                        value={formatNumberWithCommas(maxSalary)}
                        onChange={(e) =>
                          handleFieldChange("maxSalary", parseNumberFromCommas(e.target.value))
                        }
                        placeholder={t("maxSalaryOptional")}
                        error={
                          errors.maxSalary && touched.maxSalary
                            ? errors.maxSalary
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips Section */}
              <div className="rounded-xl bg-primaryLighter/5 p-3 sm:p-4 border border-primaryLighter/20">
                <div className="flex items-start gap-2 sm:gap-3">
                  <CheckmarkCircle01Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-xs sm:text-sm text-primary">
                    <p className="font-medium mb-1">
                      💡 {t("applicationTips")}:
                    </p>
                    <ul className="space-y-0.5 sm:space-y-1 text-primary">
                      <li>{t("applicationTipsDesc")}</li>
                      <li>{t("applicationTipsDesc2")}</li>
                      <li>{t("applicationTipsDesc3")}</li>
                      <li>{t("applicationTipsDesc4")}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 sm:pt-4">
                <button
                  type="button"
                  className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl border-2 border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 transition-all duration-200 text-sm sm:text-base"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-primary to-primaryDark text-white font-semibold hover:from-primaryDark hover:to-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <CircularProgress />
                      {t("applying")}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <CheckmarkCircle01Icon className="h-4 w-4" />
                      {t("applyNow")}
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default ApplyJobModal;
